import { seriesSvgPoint } from '@d3fc/d3fc-series';
import { OutliersDataItem } from 'widgets/outliers/data/outliersData';
import { BaseType, select, Selection } from 'd3-selection';
import { getSizeScale } from './size';
import { extentLinear } from '@d3fc/d3fc-extent';
import { BubbleMouseOver } from 'widgets/outliers/components/chart/BubbleMouseOver';
import { Dimensions } from 'utils/types/Dimensions';
import { getOutliersAccessors } from 'widgets/outliers/components/chart/axisStrategies';

export const buildBubblesSeries = (
    flipAxis: boolean | undefined,
    plotAreaDimensions: Dimensions,
    dataItems: OutliersDataItem[],
    bubbleMouseOver: BubbleMouseOver,
    selectedOrderIds: string[]
) => {
    const { scale: sizeScale, max: bubbleMaxPx } = getSizeScale(
        plotAreaDimensions.width,
        plotAreaDimensions.height,
        dataItems
    );

    const { xAccessor, yAccessor } = getOutliersAccessors(flipAxis);

    const pointSymbol = {
        draw: function (context: CanvasRenderingContext2D, size: number) {
            context.lineWidth = 1;
            context.arc(0, 0, size / 2, 0, 2 * Math.PI);
            context.closePath();
        },
    };

    const mouseOverIdPrefix = 'bubble-order-id';
    const mouseOverId = (i: OutliersDataItem) =>
        `${mouseOverIdPrefix}_${i.orderID}`;

    return {
        bubbleMaxPx,
        series: seriesSvgPoint()
            .crossValue(xAccessor)
            .mainValue(yAccessor)
            .size((d: OutliersDataItem) => sizeScale(d.value))
            .type(pointSymbol)
            .decorate(
                (s: Selection<BaseType, OutliersDataItem, null, unknown>) => {
                    s.classed('order-point', true);
                    s.enter()
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .merge(s as any)
                        .classed('side-B', (d) => d.side === 'B')
                        .classed('side-S', (d) => d.side === 'S')
                        .classed('side-SS', (d) => d.side === 'SS')
                        .classed(
                            'selected',
                            (d) =>
                                selectedOrderIds.indexOf(d.orderID.toString()) >
                                -1
                        );

                    s.enter()
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .merge(s as any)
                        .call(() => {
                            bubbleMouseOver.unsubscribeAllWithPrefix(
                                mouseOverIdPrefix
                            );
                        })
                        .each((data, index, nodes) => {
                            const pathSelection = select(nodes[index]).select(
                                'path'
                            );

                            bubbleMouseOver.subscribe(
                                mouseOverId(data),
                                (e) => {
                                    const highlighted =
                                        e!.orderIds.indexOf(
                                            data.orderID.toString()
                                        ) > -1;
                                    const parentSelection = select(
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        (pathSelection.node()! as any)
                                            .parentNode
                                    );
                                    parentSelection.classed(
                                        'highlighted',
                                        highlighted
                                    );
                                }
                            );

                            pathSelection.attr('bubble-order-id', data.orderID);
                        });

                    s.exit().each((data) => {
                        bubbleMouseOver.unsubscribe(
                            mouseOverId(data as OutliersDataItem)
                        );
                    });
                }
            ),
    };
};

export function linearExtentAdjustedForBubble<T>(
    dataItems: T[],
    prop: keyof T,
    bubbleMaxPx: number,
    lengthPx: number,
    mirror: boolean
) {
    const values = dataItems.map((d) => d[prop] as unknown as number);
    let max = Math.max(...values);
    let min = Math.min(...values);
    if (mirror) {
        const absMax = Math.max(Math.abs(max), Math.abs(min));
        max = absMax;
        min = absMax * -1;
    }
    const bubbleAsPerf = ((max - min) / lengthPx) * bubbleMaxPx;

    const scaleMax = max + 10 + bubbleAsPerf / 2;
    const scaleMin = min - 10 - bubbleAsPerf / 2;

    return extentLinear()
        .accessors([(a: T) => a[prop]])
        .include([scaleMin, scaleMax])
        .pad([0.05, 0.05]);
}
